/**
 * A single metric (or paired A/B metric) rendered as a paper card on the
 * drawer's schematic grid. Singles show one big latest value + sparkline;
 * pairs fold treatment/control side-by-side with a highlighted delta. Both
 * expand to a full time-series chart — for a pair, both arms overlay on one
 * chart.
 */
import React from 'react';
import { LineChart, Sparkline, Text, useToast } from '@chatsdet/ui';
import { ArrowClockwise, CaretRight, X } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  useCollectMetric,
  useMetricDatapoints,
  type MetricDatapoint,
  type ProductCardMetric,
} from '../hooks/useProductCardMetrics';
import {
  formatLatest,
  formatScalar,
  metricTitle,
  pairDeltaTone,
  toneClass,
  valueTone,
  type MetricGroup,
} from './metricPairing';

const TREATMENT_COLOR = '#4DA6FF';
const CONTROL_COLOR = '#94A3B8';

interface CollectMetricToast {
  info: (message: string) => void;
  error: (message: string, options?: { reportError?: boolean }) => void;
  success: (message: string) => void;
}

interface CollectMetricResult {
  skipped: boolean;
  reason?: string;
  datapoint: MetricDatapoint | null;
}

export function showCollectMetricResultToast(
  toast: CollectMetricToast,
  metricName: string,
  result: CollectMetricResult
) {
  if (result.skipped) {
    toast.info(result.reason ?? 'Collection unavailable in this environment');
    return;
  }
  if (result.datapoint?.error) {
    toast.error(`Collect failed: ${result.datapoint.error}`, { reportError: false });
    return;
  }
  toast.success(`Collected ${metricName}`);
}

/** Compact axis label for a datapoint timestamp (M/D plus H:MM). */
function formatTick(capturedAt: string): string {
  const d = new Date(capturedAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface Props {
  group: MetricGroup;
  productItemId: string;
  /** Batched sparkline series for this card, keyed by metric machine-name. */
  sparkByName: Map<string, number[]>;
  onUnlink: (name: string) => void;
  unlinkPending: boolean;
}

export function MetricGroupCard({
  group,
  productItemId,
  sparkByName,
  onUnlink,
  unlinkPending,
}: Props) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="overflow-hidden rounded-lg border border-border/70 bg-card shadow-sm">
      {group.kind === 'single' ? (
        <SingleCard
          metric={group.metric}
          productItemId={productItemId}
          sparkValues={sparkByName.get(group.metric.name) ?? []}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          onUnlink={onUnlink}
          unlinkPending={unlinkPending}
        />
      ) : (
        <PairCard
          group={group}
          productItemId={productItemId}
          sparkByName={sparkByName}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          onUnlink={onUnlink}
          unlinkPending={unlinkPending}
        />
      )}
    </div>
  );
}

/** Refresh-now + unlink icon buttons for one metric. */
function MetricActions({
  metric,
  productItemId,
  onUnlink,
  unlinkPending,
}: {
  metric: ProductCardMetric;
  productItemId: string;
  onUnlink: (name: string) => void;
  unlinkPending: boolean;
}) {
  const { toast } = useToast();
  const collect = useCollectMetric(productItemId);
  return (
    <div className="flex shrink-0 items-center">
      <button
        type="button"
        onClick={() =>
          collect.mutate(metric.name, {
            onSuccess: (r) => {
              showCollectMetricResultToast(toast, metric.name, r);
            },
            onError: (err) => toast.error(err.message),
          })
        }
        disabled={collect.isPending}
        title={`Collect ${metric.name} now`}
        aria-label={`Collect ${metric.name} now`}
        className="grid size-7 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
      >
        <ArrowClockwise size={13} className={collect.isPending ? 'animate-spin' : ''} />
      </button>
      <button
        type="button"
        onClick={() => onUnlink(metric.name)}
        disabled={unlinkPending}
        title={`Unlink ${metric.name}`}
        aria-label={`Unlink ${metric.name}`}
        className="grid size-7 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}

/** Large latest value with a de-emphasized unit; tone-colored when directional. */
function LatestValue({
  metric,
  size = 'lg',
}: {
  metric: ProductCardMetric;
  size?: 'lg' | 'md';
}) {
  const { value, unit } = formatLatest(metric);
  const numeric = metric.lastError === null ? metric.lastValue : null;
  const tone = numeric !== null ? valueTone(metric, numeric) : 'neutral';
  const valueClass = size === 'lg' ? 'text-2xl' : 'text-xl';
  // Directional metrics (deltas, ratios) take a good/bad hue; everything else
  // stays strong-foreground rather than the muted neutral tone.
  const colorClass = tone === 'neutral' ? 'text-foreground' : toneClass(tone);
  return (
    <div className="flex items-baseline gap-1.5 tabular-nums">
      <span
        className={
          metric.lastError
            ? 'text-sm font-medium text-[#FFBD0F]'
            : `${valueClass} font-semibold leading-none ${colorClass}`
        }
        title={metric.lastError ?? undefined}
      >
        {value}
      </span>
      {unit && <span className="text-xs font-medium text-muted-foreground">{unit}</span>}
    </div>
  );
}

function ExpandChevron({ expanded }: { expanded: boolean }) {
  return (
    <CaretRight
      size={13}
      weight="bold"
      className={`shrink-0 text-muted-foreground transition-transform duration-200 ${
        expanded ? 'rotate-90' : ''
      }`}
    />
  );
}

function SingleCard({
  metric,
  productItemId,
  sparkValues,
  expanded,
  onToggle,
  onUnlink,
  unlinkPending,
}: {
  metric: ProductCardMetric;
  productItemId: string;
  sparkValues: number[];
  expanded: boolean;
  onToggle: () => void;
  onUnlink: (name: string) => void;
  unlinkPending: boolean;
}) {
  const datapointsQuery = useMetricDatapoints(expanded ? metric.name : null);
  const chartData = (datapointsQuery.data ?? [])
    .filter((d) => d.value !== null)
    .map((d) => ({ t: formatTick(d.capturedAt), value: d.value as number }));

  return (
    <>
      <div className="flex items-center gap-3 px-3.5 py-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          aria-expanded={expanded}
        >
          <ExpandChevron expanded={expanded} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-balance text-foreground">
              {metricTitle(metric)}
            </span>
            <span className="truncate font-mono text-[11px] text-muted-foreground">
              {metric.name}
            </span>
            {metric.description && (
              <span className="line-clamp-1 text-pretty text-xs text-muted-foreground">
                {metric.description}
              </span>
            )}
          </span>
          {sparkValues.length > 0 && (
            <Sparkline points={sparkValues} width={76} height={26} className="shrink-0" />
          )}
          <LatestValue metric={metric} />
        </button>
        <MetricActions
          metric={metric}
          productItemId={productItemId}
          onUnlink={onUnlink}
          unlinkPending={unlinkPending}
        />
      </div>
      <ExpandedChart
        expanded={expanded}
        loading={datapointsQuery.isLoading}
        empty={chartData.length === 0}
        data={chartData}
        lines={[{ dataKey: 'value', name: metric.unit ?? 'value', color: TREATMENT_COLOR }]}
        showLegend={false}
        description={metric.description ?? undefined}
      />
    </>
  );
}

function PairCard({
  group,
  productItemId,
  sparkByName,
  expanded,
  onToggle,
  onUnlink,
  unlinkPending,
}: {
  group: Extract<MetricGroup, { kind: 'pair' }>;
  productItemId: string;
  sparkByName: Map<string, number[]>;
  expanded: boolean;
  onToggle: () => void;
  onUnlink: (name: string) => void;
  unlinkPending: boolean;
}) {
  const { treatment, control, label, root } = group;

  // Delta is meaningful only when both arms have a current value.
  const delta =
    treatment.lastError === null &&
    control.lastError === null &&
    treatment.lastValue !== null &&
    control.lastValue !== null
      ? treatment.lastValue - control.lastValue
      : null;
  const deltaUnit = (treatment.unit ?? '').toLowerCase() === 'percent' ? 'pp' : treatment.unit;
  const deltaTone = delta !== null ? pairDeltaTone(root, treatment.unit, delta) : 'neutral';

  const tDatapoints = useMetricDatapoints(expanded ? treatment.name : null);
  const cDatapoints = useMetricDatapoints(expanded ? control.name : null);
  const merged = mergeArmDatapoints(tDatapoints.data, cDatapoints.data);

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 border-b border-border/60 px-3.5 py-2.5 text-left"
        aria-expanded={expanded}
      >
        <ExpandChevron expanded={expanded} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-balance text-foreground">{label}</span>
          <span className="truncate font-mono text-[11px] text-muted-foreground">{root}</span>
        </span>
        {delta !== null && (
          <span
            className={`shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums ${toneClass(deltaTone)}`}
            title="Treatment − Control"
          >
            {delta >= 0 ? '+' : '−'}
            {formatScalar(Math.abs(delta))}
            {deltaUnit ? ` ${deltaUnit}` : ''}
          </span>
        )}
      </button>

      <div className="grid grid-cols-2 divide-x divide-border/60">
        <ArmColumn
          arm="Treatment"
          accent={TREATMENT_COLOR}
          metric={treatment}
          productItemId={productItemId}
          sparkValues={sparkByName.get(treatment.name) ?? []}
          onUnlink={onUnlink}
          unlinkPending={unlinkPending}
        />
        <ArmColumn
          arm="Control"
          accent={CONTROL_COLOR}
          metric={control}
          productItemId={productItemId}
          sparkValues={sparkByName.get(control.name) ?? []}
          onUnlink={onUnlink}
          unlinkPending={unlinkPending}
        />
      </div>

      <ExpandedChart
        expanded={expanded}
        loading={tDatapoints.isLoading || cDatapoints.isLoading}
        empty={merged.length === 0}
        data={merged}
        lines={[
          { dataKey: 'treatment', name: 'Treatment', color: TREATMENT_COLOR },
          { dataKey: 'control', name: 'Control', color: CONTROL_COLOR },
        ]}
        showLegend
      />
    </>
  );
}

function ArmColumn({
  arm,
  accent,
  metric,
  productItemId,
  sparkValues,
  onUnlink,
  unlinkPending,
}: {
  arm: string;
  accent: string;
  metric: ProductCardMetric;
  productItemId: string;
  sparkValues: number[];
  onUnlink: (name: string) => void;
  unlinkPending: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2 px-3.5 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
          {arm}
        </span>
        <MetricActions
          metric={metric}
          productItemId={productItemId}
          onUnlink={onUnlink}
          unlinkPending={unlinkPending}
        />
      </div>
      <div className="flex items-end justify-between gap-2">
        <LatestValue metric={metric} size="md" />
        {sparkValues.length > 0 && (
          <Sparkline points={sparkValues} width={64} height={22} className="shrink-0" />
        )}
      </div>
    </div>
  );
}

/** Shared animated time-series panel shown when a card is expanded. */
function ExpandedChart({
  expanded,
  loading,
  empty,
  data,
  lines,
  showLegend,
  description,
}: {
  expanded: boolean;
  loading: boolean;
  empty: boolean;
  data: Array<Record<string, string | number>>;
  lines: Array<{ dataKey: string; name: string; color: string }>;
  showLegend: boolean;
  description?: string;
}) {
  return (
    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="border-t border-border/60 p-3">
            {description && (
              <Text size="xs" className="mb-2 text-pretty text-muted-foreground">
                {description}
              </Text>
            )}
            {loading ? (
              <Text size="xs" className="text-muted-foreground">
                Loading…
              </Text>
            ) : empty ? (
              <Text size="xs" className="text-muted-foreground">
                No datapoints yet.
              </Text>
            ) : (
              <div className="w-full">
                <LineChart
                  data={data}
                  xDataKey="t"
                  lines={lines}
                  showLegend={showLegend}
                  showYAxis
                  height={170}
                  xAxisInterval="preserveStartEnd"
                  dot={false}
                />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Merge two arms' datapoints into rows keyed by formatted tick, time-ordered. */
function mergeArmDatapoints(
  treatment: MetricDatapoint[] | undefined,
  control: MetricDatapoint[] | undefined
): Array<{ t: string; treatment?: number; control?: number }> {
  const rows = new Map<string, { t: string; order: number; treatment?: number; control?: number }>();
  const add = (points: MetricDatapoint[] | undefined, arm: 'treatment' | 'control') => {
    for (const d of points ?? []) {
      if (d.value === null) continue;
      const t = formatTick(d.capturedAt);
      const order = new Date(d.capturedAt).getTime();
      const row = rows.get(t) ?? { t, order };
      row[arm] = d.value;
      if (!Number.isNaN(order)) row.order = Math.min(row.order, order);
      rows.set(t, row);
    }
  };
  add(treatment, 'treatment');
  add(control, 'control');
  return [...rows.values()]
    .sort((a, b) => a.order - b.order)
    .map(({ t, treatment: tv, control: cv }) => ({ t, treatment: tv, control: cv }));
}
